import { QueryType, ShopifyLookupResource } from "../types";
import {
  fetchMostRecentOrder,
  fetchOpenOrder,
  fetchProducts,
  fetchInventoryByLocation,
  ShopifyOrderSummary,
  ShopifyProductSummary,
  ShopifyLocationSummary,
} from "../shopify/client";
import { SHOPIFY_PERSONA_ID_PREFIX } from "../data/personas";

export const SHOPIFY_LOOKUP_BY_QUERY: Record<QueryType, { resource: ShopifyLookupResource; label: string } | null> = {
  "wismo":        { resource: "open_order",   label: "Looking up your open order" },
  "cancellation": { resource: "recent_order", label: "Looking up your recent order" },
  "store-desk":   { resource: "inventory",    label: "Checking in-store availability" },
  "product-rec":  { resource: "products",     label: "Browsing recommended products" },
  // Airline queries don't use Shopify — the passenger profile comes from the crew's Supabase lookup.
  "refund-status":         null,
  "baggage-reimbursement": null,
  // BFSI / Fintech queries don't use Shopify either — the account comes from the Fintech backend.
  "returned-payment":      null,
  "compliance-hold":       null,
  "missing-invoice":       null,
  "integration-sync":      null,
};

// Picked-from-Shopify personas use id = `${SHOPIFY_PERSONA_ID_PREFIX}<numericId>`.
export function shopifyCustomerIdFromPersonaId(personaId: string | null): number | null {
  if (!personaId || !personaId.startsWith(SHOPIFY_PERSONA_ID_PREFIX)) return null;
  const n = parseInt(personaId.slice(SHOPIFY_PERSONA_ID_PREFIX.length), 10);
  return Number.isFinite(n) ? n : null;
}

export type ShopifyLookupResult =
  | { kind: "order";     order: ShopifyOrderSummary }
  | { kind: "products";  products: ShopifyProductSummary[] }
  | { kind: "inventory"; product: ShopifyProductSummary | null; locations: ShopifyLocationSummary[] }
  | { kind: "empty" };

export async function runShopifyLookup(
  resource: ShopifyLookupResource,
  scope: { customerId?: number; email?: string },
): Promise<ShopifyLookupResult> {
  if (resource === "recent_order") {
    const order = await fetchMostRecentOrder(scope);
    return order ? { kind: "order", order } : { kind: "empty" };
  }
  if (resource === "open_order") {
    const order = await fetchOpenOrder(scope);
    return order ? { kind: "order", order } : { kind: "empty" };
  }
  if (resource === "products") {
    const products = await fetchProducts(4);
    return { kind: "products", products };
  }
  const inv = await fetchInventoryByLocation();
  return { kind: "inventory", product: inv.product, locations: inv.locations };
}

export function shopifyLookupQueryKey(
  resource: ShopifyLookupResource,
  scope: { customerId?: number; email?: string },
) {
  return ["shopify_lookup", resource, scope.customerId ?? null, scope.email ?? null] as const;
}
